/**
 * The one-time sheet a phone visitor sees (ROADMAP "The phone branch",
 * item 3). It replaces the old "Best on Desktop" dialog, which apologised for
 * the whole app before a reader saw the research they came for. This one
 * says what the phone does, once: research and read here, analyse on a
 * desktop.
 *
 * Shown on a TOUCH phone only — width alone would fire it on a desktop window
 * dragged narrow during development. Dismissed for good (localStorage), not
 * per session: "once" should mean once.
 *
 * Shown on the reader's SECOND page view of the visit, never the first (owner
 * decision B, 2026-09-28): a reader arriving from search gets the page they
 * searched for unobstructed — the arrival case Google's intrusive-interstitial
 * guidance targets — and the note arrives once they are browsing.
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Button, Drawer, Typography } from '@mui/material';
import ShareLinkButton from './ShareLinkButton';
import { usePhoneViewport } from './usePhoneViewport';
import { markWelcomeShownThisVisit } from './installPrompt';

/** Kept from the old dialog so a reader who dismissed it this session is not asked again. */
export const PHONE_SHEET_KEY = 'indexalpha-mobile-modal-dismissed';

function alreadyDismissed(): boolean {
  try {
    return localStorage.getItem(PHONE_SHEET_KEY) !== null || sessionStorage.getItem(PHONE_SHEET_KEY) !== null;
  } catch {
    // Storage blocked (private mode, site data off): say it once per page load.
    return false;
  }
}

function rememberDismissed(): void {
  try {
    localStorage.setItem(PHONE_SHEET_KEY, 'true');
  } catch {
    // Nothing to do — the sheet simply shows again next visit.
  }
}

/** Page views this visit (sessionStorage), counted on each route change. */
export const PHONE_VIEWS_KEY = 'indexalpha-phone-page-views';
const LAST_PATH_KEY = 'indexalpha-phone-last-path';

/**
 * Count a view of `pathname` and return the visit's running total (1 when
 * storage is blocked). Idempotent per path: a second call for the path already
 * counted — React StrictMode running the effect twice, or a reload — is not a
 * second page view. Without that, the dev build showed the sheet on arrival.
 */
function countPageView(pathname: string): number {
  try {
    const views = Number(sessionStorage.getItem(PHONE_VIEWS_KEY) || '0');
    if (sessionStorage.getItem(LAST_PATH_KEY) === pathname && views > 0) return views;
    sessionStorage.setItem(LAST_PATH_KEY, pathname);
    sessionStorage.setItem(PHONE_VIEWS_KEY, String(views + 1));
    return views + 1;
  } catch {
    return 1;
  }
}

function isTouch(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
}

const PhoneWelcomeSheet: React.FC = () => {
  const isPhone = usePhoneViewport();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const views = countPageView(pathname);
    if (views >= 2 && isPhone && isTouch() && !alreadyDismissed()) {
      setOpen(true);
      // So the install sheet (third view) never lands in the same visit.
      markWelcomeShownThisVisit();
    }
  }, [pathname, isPhone]);

  const dismiss = () => {
    setOpen(false);
    rememberDismissed();
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={dismiss}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          px: 3,
          pt: 3,
          pb: 'calc(24px + env(safe-area-inset-bottom, 0px))',
        },
      }}
    >
      <Typography component="h2" variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        Research and read here. Analyse on a desktop.
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        Look up any stock and read the letter on your phone. Your book&rsquo;s risk,
        rebalancing and uploads need a wide screen.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Button
          variant="contained"
          onClick={dismiss}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: 'text.primary',
            color: 'background.paper',
            '&:hover': { bgcolor: 'text.secondary' },
          }}
        >
          Got it
        </Button>
        <ShareLinkButton source="phone_welcome_sheet" variant="outlined" />
      </Box>
    </Drawer>
  );
};

export default PhoneWelcomeSheet;
